import { toast } from 'sonner';
import { useCompleteTask, useUncompleteTask, type Task } from './queries/useTasks';

type TaskRef = Pick<Task, 'id' | 'content' | 'is_completed'>;

function fail(err: unknown) {
  toast.error(err instanceof Error ? err.message : 'Impossible de modifier la tâche');
}

/**
 * Termine ou rouvre une tâche, partout de la même façon : terminer affiche
 * un message avec « Annuler », pour rattraper un clic malheureux.
 */
export function useTaskCompletion() {
  const complete = useCompleteTask();
  const uncomplete = useUncompleteTask();

  function finish(task: TaskRef) {
    complete.mutate(task.id, {
      onSuccess: () =>
        toast(`Tâche terminée — « ${task.content} »`, {
          action: { label: 'Annuler', onClick: () => uncomplete.mutate(task.id, { onError: fail }) },
        }),
      onError: fail,
    });
  }

  function reopen(task: TaskRef) {
    uncomplete.mutate(task.id, { onError: fail });
  }

  /** Plusieurs tâches d'un coup (actions groupées) : un seul message, un seul « Annuler ». */
  async function finishMany(tasks: TaskRef[]) {
    const results = await Promise.allSettled(tasks.map((t) => complete.mutateAsync(t.id)));
    const doneIds = tasks.filter((_, i) => results[i]?.status === 'fulfilled').map((t) => t.id);
    if (doneIds.length < tasks.length) toast.error("Certaines tâches n'ont pas pu être modifiées");
    if (doneIds.length === 0) return;
    toast(doneIds.length > 1 ? `${doneIds.length} tâches terminées` : `Tâche terminée — « ${tasks.find((t) => t.id === doneIds[0])?.content ?? ''} »`, {
      action: { label: 'Annuler', onClick: () => doneIds.forEach((id) => uncomplete.mutate(id, { onError: fail })) },
    });
  }

  async function reopenMany(tasks: TaskRef[]) {
    const results = await Promise.allSettled(tasks.map((t) => uncomplete.mutateAsync(t.id)));
    if (results.some((r) => r.status === 'rejected')) toast.error("Certaines tâches n'ont pas pu être modifiées");
  }

  return {
    finish,
    reopen,
    toggle: (task: TaskRef) => (task.is_completed ? reopen(task) : finish(task)),
    finishMany,
    reopenMany,
    isPending: complete.isPending || uncomplete.isPending,
  };
}
